import { renderToStaticMarkup } from 'react-dom/server';
import type {
  CreditNoteHeader,
  CreditNoteItem,
} from '@/utils/approval/creditNote';

/** 작성·조회 화면이 이 클래스로 DETAIL·삭제 버튼을 그림 */
export const CARD_CLASS = 'cn-card';
export const ACTION_CELL_CLASS = 'cn-action';

const text = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim();

/** 레거시 number_format — 못 읽는 값은 그대로 */
const formatNumber = (v: unknown, digits: number) => {
  const raw = text(v);
  const n = Number(raw.replace(/,/g, ''));
  return raw && Number.isFinite(n)
    ? n.toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    : raw;
};

const HeaderRow = ({ row }: { row: CreditNoteHeader }) => (
  <tr className="text-center">
    <td>{text(row.brandNm)}</td>
    <td>{text(row.custNm)}</td>
    <td>{text(row.styleCnt)}</td>
    <td>{text(row.colorCnt)}</td>
    <td>{formatNumber(row.creditQty, 0)}</td>
    <td>{formatNumber(row.creditAmt, 2)}</td>
    <td>{text(row.currCd)}</td>
  </tr>
);

/** 레거시 appr_sub_view_forms_credit_note_mstr — 건마다 카드 1개. 표 머리글은 레거시 영문 그대로 */
const Card = ({ item }: { item: CreditNoteItem }) => {
  const { card } = item;
  const headers = card.headers ?? [];
  const oz = card.ozPrint ?? {};
  // 레거시 머리줄 유형은 첫 행의 유형명(X040 코드명)
  const typeName = text(headers[0]?.typeNm);
  return (
    // 백엔드가 부분 반려된 카드를 조회 때 이 data-item-id 로 찾아 뺌
    <div
      className={CARD_CLASS}
      data-item-id={item.aprvId}
      data-label={item.label}
      data-aprv-rev={item.aprvRev}
    >
      <table className="table table-bordered">
        <colgroup>
          <col width="10%" />
          <col width="17%" />
          <col width="10%" />
          <col width="15%" />
          <col width="15%" />
          <col width="16%" />
          <col width="16%" />
        </colgroup>
        <tbody>
          <tr>
            <th className="text-center bg-info" colSpan={5}>
              {`${text(card.creditNoBiz) || item.label}-${typeName}`}
            </th>
            <th
              className={`text-right bg-info ${ACTION_CELL_CLASS}`}
              colSpan={2}
              data-oz-url={text(oz.url)}
              data-oz-ozr={text(oz.gerpOzrNm)}
              data-oz-odi={text(oz.gerpOdiNm)}
              data-oz-pcount={text(oz.pcount)}
              data-oz-args1={text(oz.args1)}
              data-oz-locale={text(oz.locale)}
            />
          </tr>
          {headers.length > 0 && (
            <>
              <tr>
                <th className="text-center bg-success" colSpan={7}>
                  Summary
                </th>
              </tr>
              <tr className="bg-title">
                <td rowSpan={2}>BRAND</td>
                <td rowSpan={2}>CUSTOMER</td>
                <td colSpan={5}>TOTAL</td>
              </tr>
              <tr className="bg-title">
                <td>STYLE</td>
                <td>STYLE-COLOR</td>
                <td>CREDIT QUANTITY</td>
                <td>CREDIT AMOUNT</td>
                <td>CURRENCY</td>
              </tr>
              {headers.map((row, i) => (
                <HeaderRow key={i} row={row} />
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

/** 저장 본문 — 고른 순서대로 카드 */
export const buildCreditNoteHtml = (items: CreditNoteItem[]): string =>
  items.length
    ? renderToStaticMarkup(
        <>
          {items.map((item) => (
            <Card key={item.aprvId} item={item} />
          ))}
        </>,
      )
    : '';
